// ─── Shred Calculator — Science-backed engine ────────────────────────────────
// Formulas: Katch-McArdle (with BF%) | Mifflin-St Jeor (fallback)
// All internal math uses kg + cm. UI can pass lbs/inches — converted before entry.

export type Gender       = 'male' | 'female'
export type UnitSystem   = 'imperial' | 'metric'
export type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'active' | 'extreme'
export type DeficitGoal  = 'lean' | 'balanced' | 'aggressive'

export interface ShredInputs {
  gender:      Gender
  units:       UnitSystem
  weightLbs:   number   // always lbs internally
  heightCm:    number   // always cm internally
  age:         number
  useBodyFat:  boolean
  bodyFat:     number   // 4–45 %
  activity:    ActivityLevel
  goal:        DeficitGoal
}

export interface ShredResults {
  // Calorie targets
  bmr:             number
  tdee:            number
  deficitPct:      number   // integer e.g. 20
  dailyCalories:   number
  formula:         'Katch-McArdle' | 'Mifflin-St Jeor'
  // Lean mass
  leanBodyMassLbs: number | null
  // Macros (grams + cals)
  protein:         number
  proteinCals:     number
  fats:            number
  fatsCals:        number
  carbs:           number
  carbsCals:       number
  // Extras
  ffmi:            number | null
  ffmiClass:       string | null
  waterLiters:     number
  waterCups:       number
  weeksToLean:     number
  targetBodyFat:   number
}

// ─── Constants ───────────────────────────────────────────────────────────────

export const LBS_PER_KG  = 2.20462
export const CM_PER_INCH = 2.54

export const ACTIVITY_FACTORS: Record<ActivityLevel, number> = {
  sedentary: 1.2,
  light:     1.375,
  moderate:  1.55,
  active:    1.725,
  extreme:   1.9,
}

export const ACTIVITY_LABELS: Record<ActivityLevel, { label: string; desc: string }> = {
  sedentary: { label: 'Sedentary',  desc: 'Little to no exercise, desk job'             },
  light:     { label: 'Light',      desc: 'Light exercise 1–3 days / week'              },
  moderate:  { label: 'Moderate',   desc: 'Moderate exercise 3–5 days / week'           },
  active:    { label: 'Active',     desc: 'Hard exercise 6–7 days / week'               },
  extreme:   { label: 'Extreme',    desc: 'Athlete, physical job, or 2× daily training' },
}

export const GOAL_CONFIG: Record<DeficitGoal, { label: string; pct: number; desc: string }> = {
  lean:       { label: 'Lean Cut',         pct: 0.15, desc: '15% deficit — preserve maximum muscle, slow steady shred' },
  balanced:   { label: 'Balanced',         pct: 0.20, desc: '20% deficit — steady sustainable shred, recommended for most' },
  aggressive: { label: 'Aggressive Shred', pct: 0.25, desc: '25% deficit — fastest fat loss, requires disciplined training' },
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Return n if finite and ≥ 0, otherwise fallback */
function safe(n: number, fallback: number): number {
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n))
}

function classifyFFMI(ffmi: number, gender: Gender): string {
  if (gender === 'male') {
    if (ffmi < 18)  return 'Below average'
    if (ffmi < 20)  return 'Average'
    if (ffmi < 22)  return 'Above average'
    if (ffmi < 23)  return 'Excellent'
    if (ffmi < 26)  return 'Superior'
    return 'Elite / exceptional'
  }
  // female
  if (ffmi < 15)   return 'Below average'
  if (ffmi < 17)   return 'Average'
  if (ffmi < 18.5) return 'Above average'
  if (ffmi < 20)   return 'Excellent'
  if (ffmi < 22)   return 'Superior'
  return 'Elite / exceptional'
}

// ─── Main Calculation ─────────────────────────────────────────────────────────

export function calculateShred(inp: ShredInputs): ShredResults {

  // 1. Sanitise all inputs against blank / mid-type zero states
  const weightLbs  = clamp(safe(inp.weightLbs, 185),  60,  700)
  const heightCm   = clamp(safe(inp.heightCm,  175),  100, 280)
  const age        = clamp(safe(inp.age,        28),   14,  100)
  const bodyFatPct = clamp(safe(inp.bodyFat,    18),   4,   45)

  const weightKg  = weightLbs / LBS_PER_KG
  const heightM   = heightCm  / 100

  // ── 2. BMR ────────────────────────────────────────────────────────────────
  let bmr:             number
  let formula:         ShredResults['formula']
  let leanBodyMassLbs: number | null = null

  if (inp.useBodyFat) {
    // Katch-McArdle  BMR = 370 + 21.6 × LBM(kg)
    const lbmKg     = weightKg * (1 - bodyFatPct / 100)
    leanBodyMassLbs = Math.round(lbmKg * LBS_PER_KG)
    bmr             = 370 + 21.6 * lbmKg
    formula         = 'Katch-McArdle'
  } else {
    // Mifflin-St Jeor  BMR = 10×kg + 6.25×cm − 5×age + sex_constant
    const base = 10 * weightKg + 6.25 * heightCm - 5 * age
    bmr    = inp.gender === 'male' ? base + 5 : base - 161
    formula = 'Mifflin-St Jeor'
  }

  // Safety floor — no BMR below 800 kcal for a real human
  bmr = Math.max(800, safe(bmr, 1500))

  // ── 3. TDEE + Deficit ─────────────────────────────────────────────────────
  const actFactor    = ACTIVITY_FACTORS[inp.activity]
  const tdee         = bmr * actFactor
  const deficitFrac  = GOAL_CONFIG[inp.goal].pct          // e.g. 0.20
  const deficitPct   = Math.round(deficitFrac * 100)       // e.g. 20
  const dailyCalories = Math.round(tdee * (1 - deficitFrac))

  // ── 4. Macros ─────────────────────────────────────────────────────────────
  // Protein: 1.1 g per lb of LBM (if available) or total body weight — MINIMUM 50g
  const proteinBasis = leanBodyMassLbs ?? weightLbs
  const protein      = Math.max(50, Math.round(proteinBasis * 1.1))
  const proteinCals  = protein * 4

  // Fats: 22.5% of daily calories — MINIMUM 30g
  const fatsCalRaw = Math.round(dailyCalories * 0.225)
  const fats       = Math.max(30, Math.round(fatsCalRaw / 9))
  const fatsCals   = fats * 9

  // Carbs: remainder calories (floor at 0g)
  const carbsCals = Math.max(0, dailyCalories - proteinCals - fatsCals)
  const carbs     = Math.round(carbsCals / 4)

  // ── 5. FFMI ───────────────────────────────────────────────────────────────
  // Normalised FFMI (Kouri 1995) = LBM(kg) / height(m)² + 6.1 × (1.8 − h(m))
  let ffmi:      number | null = null
  let ffmiClass: string | null = null

  if (inp.useBodyFat && leanBodyMassLbs !== null && leanBodyMassLbs > 0) {
    const lbmKg      = leanBodyMassLbs / LBS_PER_KG
    const rawFfmi    = lbmKg / (heightM * heightM)
    const normalized = rawFfmi + 6.1 * (1.8 - heightM)
    ffmi      = Math.round(safe(normalized, 0) * 10) / 10
    ffmiClass = classifyFFMI(ffmi, inp.gender)
  }

  // ── 6. Water Intake ───────────────────────────────────────────────────────
  // Base: 35 ml/kg bodyweight, scaled up with activity (up to ~40 ml/kg for extreme)
  // actFactor range: 1.2 (sedentary) → 1.9 (extreme)  →  scale range: 1.0 → 1.28
  const activityScale = 1 + (actFactor - 1.2) * 0.4
  const waterLiters   = Math.round(weightKg * 0.035 * activityScale * 10) / 10
  const waterCups     = Math.round(waterLiters * 4.2268)   // 1 L = 4.2268 US cups

  // ── 7. Shred Timeline ─────────────────────────────────────────────────────
  // Target body fat: 10% male / 18% female (lean and ripped threshold)
  // Weekly fat loss = (daily deficit kcal × 7) ÷ 7,700 kcal per kg of fat
  const targetBodyFat   = inp.gender === 'male' ? 10 : 18
  const dailyDeficitCal = safe(tdee - dailyCalories, 0)
  const weeklyFatLossKg = (dailyDeficitCal * 7) / 7700

  let weeksToLean = 0
  if (weeklyFatLossKg > 0) {
    if (inp.useBodyFat && bodyFatPct > targetBodyFat) {
      // Exact: fat mass to lose
      const fatToLoseKg = weightKg * ((bodyFatPct - targetBodyFat) / 100)
      weeksToLean = Math.round(safe(fatToLoseKg / weeklyFatLossKg, 0))
    } else if (!inp.useBodyFat) {
      // Conservative heuristic: ~5 kg visible fat to strip for "lean" look
      weeksToLean = Math.round(safe(5 / weeklyFatLossKg, 0))
    }
    // Cap at 104 weeks (2 years) to keep UI sensible
    weeksToLean = Math.min(weeksToLean, 104)
  }

  // ── 8. Return ─────────────────────────────────────────────────────────────
  return {
    bmr:             Math.round(bmr),
    tdee:            Math.round(tdee),
    deficitPct,
    dailyCalories,
    formula,
    leanBodyMassLbs,
    protein,
    proteinCals,
    fats,
    fatsCals,
    carbs,
    carbsCals,
    ffmi,
    ffmiClass,
    waterLiters,
    waterCups,
    weeksToLean,
    targetBodyFat,
  }
}
